import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const API_V1_URL = `${API_BASE_URL}/v1`;
const API_V2_URL = `${API_BASE_URL}/v2`;

interface ParamsCTXRList {
  cloud?: string;
  region?: string;
}

export function getCTXRlistGlobal(
  subscriptionId: number,
  tenant: number,
  token: string,
  cloud?: string,
  region?: string
) {
  const params: ParamsCTXRList = {};
  if (cloud !== undefined) params.cloud = cloud;
  if (region !== undefined) params.region = region;

  return axios.get(
    `${API_V1_URL}/ctxr/${subscriptionId}/listSelector?tenantId=${tenant}`,
    {
      params,
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

/**
 * Get available syslog servers for a specific CTX-R instance
 */
export function getAvailableSyslogs(
  subscriptionId: number,
  ctxrId: string,
  tenant: number,
  token: string
) {
  const url = `${API_V1_URL}/serverlog/${subscriptionId}/available/${ctxrId}?tenantId=${tenant}`;
  return axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * Get selected syslog servers for a specific CTX-R instance
 */
export function getSelectedSyslogs(
  subscriptionId: number,
  ctxrId: string,
  tenant: number,
  token: string
) {
  const url = `${API_V1_URL}/serverlog/${subscriptionId}/ctxr/${ctxrId}?tenantId=${tenant}`;
  return axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * Save syslog associations for a CTX-R instance
 */
export function saveSyslogs(
  subscriptionId: number,
  ctxrId: string,
  tenant: number,
  token: string,
  syslogs: SyslogItem[]
) {
  const url = `${API_V1_URL}/ctxr/associationwithsyslog/${ctxrId}?tenantId=${tenant}`;

  // Build payload as [ { "id": x, "level": y }, ... ]
  const payload = syslogs.map(({ id, level }) => ({
    id,
    level: level ?? 0,
  }));

  return axios.post(url, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * SyslogItem interface
 */
export interface SyslogItem {
  id: number;
  ip: string;
  port: number;
  description: string;
  level?: number;
  protocol?: "udp" | "tcp";
  reflectorInstanceCount?: number; // V2 field: 0 means available, >0 means selected
  associationId?: number; // V2 field: Single association ID (for backward compatibility)
  associationIds?: number[]; // V2 field: Array of association IDs (for cluster mode with multiple instances)
}

// ========== V2 API Functions ==========

/**
 * GET /v2/reflector/syslog-servers
 * Gets list of all syslog servers (V2 API).
 * Syslogs with reflectorInstanceCount === 0 are available.
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 * @param search - Optional search query
 */
export function getSyslogServersV2(
  tenantId: number,
  token: string,
  search?: string
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString()
  });
  if (search && search.trim() !== '') {
    params.append('search', search.trim());
  }
  return axios.get(`${API_V2_URL}/reflector/syslog-servers?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * GET /v2/syslog-servers/available?id=X&type=Y
 * Gets available syslog servers (V2 API), excluding those associated with the given id.
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 * @param id - Reflector instance ID or cluster ID (to exclude associated syslog servers)
 * @param type - Type of id: 'reflector' or 'cluster'
 * @param search - Optional search query
 */
export function getAvailableSyslogServersV2(
  tenantId: number,
  token: string,
  id: number | string,
  type: 'reflector' | 'cluster',
  search?: string
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString(),
    id: id.toString(),
    type: type
  });
  if (search && search.trim() !== '') {
    params.append('search', search.trim());
  }
  return axios.get(`${API_V2_URL}/reflector/syslog-servers/available?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * GET /v2/reflector/instances/:instanceId/syslog-servers
 * Gets selected syslog servers for a specific reflector instance (V2 API).
 * @param instanceId - Reflector instance ID
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 */
export function getSelectedSyslogsV2ForInstance(
  instanceId: string | number,
  tenantId: number,
  token: string
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString(),
    reflectorInstanceId: instanceId.toString()
  });
  return axios.get(`${API_V2_URL}/reflector/syslog-associations?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * GET /v2/reflector/clusters/:clusterId/syslog-servers
 * Gets selected syslog servers for a specific cluster (V2 API).
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 */
export function getSelectedSyslogsV2ForCluster(
  clusterId: number,
  tenantId: number,
  token: string
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString(),
    clusterId: clusterId.toString()
  });
  return axios.get(`${API_V2_URL}/reflector/syslog-associations?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * POST /v2/reflector/instances/:instanceId/syslog-servers
 * Saves syslog associations for a reflector instance (V2 API).
 * @param instanceId - Reflector instance ID
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 * @param syslogs - Array of syslog items with level
 */
export function saveSyslogsV2ForInstance(
  instanceId: string | number,
  tenantId: number,
  token: string,
  syslogs: SyslogItem[]
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString()
  });
  
  // Build payload as [ { "id": x, "level": y }, ... ]
  const payload = syslogs.map(({ id, level }) => ({
    id,
    level: level ?? 0,
  }));

  return axios.post(`${API_V2_URL}/reflector/instances/${instanceId}/syslog-servers?${params.toString()}`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * POST /v2/reflector/clusters/:clusterId/syslog-servers
 * Saves syslog associations for a cluster (V2 API).
 * @param clusterId - Cluster ID
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 * @param syslogs - Array of syslog items with level
 */
export function saveSyslogsV2ForCluster(
  clusterId: number,
  tenantId: number,
  token: string,
  syslogs: SyslogItem[]
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString()
  });
  
  // Build payload as [ { "id": x, "level": y }, ... ]
  const payload = syslogs.map(({ id, level }) => ({
    id,
    level: level ?? 0,
  }));

  return axios.post(`${API_V2_URL}/reflector/clusters/${clusterId}/syslog-servers?${params.toString()}`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * Interface for syslog association request payload (instance mode)
 */
export interface SyslogAssociationRequestInstance {
  syslogServerId: number;
  reflectorInstanceId: number;
  level: number;
}

/**
 * Interface for syslog association request payload (cluster mode)
 */
export interface SyslogAssociationRequestCluster {
  syslogServerId: number;
  clusterId: number;
  level: number;
}

/**
 * POST /v2/reflector/syslog-associations
 * Creates a syslog association for a reflector instance (V2 API).
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 * @param association - Association payload with syslogServerId, reflectorInstanceId, and level
 */
export function createSyslogAssociationForInstance(
  tenantId: number,
  token: string,
  association: SyslogAssociationRequestInstance
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString()
  });
  
  return axios.post(`${API_V2_URL}/reflector/syslog-associations?${params.toString()}`, association, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * POST /v2/reflector/syslog-associations
 * Creates a syslog association for a cluster (V2 API).
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 * @param association - Association payload with syslogServerId, clusterId, and level
 */
export function createSyslogAssociationForCluster(
  tenantId: number,
  token: string,
  association: SyslogAssociationRequestCluster
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString()
  });
  
  return axios.post(`${API_V2_URL}/reflector/syslog-associations?${params.toString()}`, association, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * PUT /v2/reflector/syslog-associations/:id
 * Updates a syslog association level (V2 API).
 * @param associationId - Association ID
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 * @param level - New log level (0-7)
 */
export function updateSyslogAssociation(
  associationId: number,
  tenantId: number,
  token: string,
  level: number
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString()
  });
  
  return axios.put(`${API_V2_URL}/reflector/syslog-associations/${associationId}?${params.toString()}`, {
    level: level
  }, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * DELETE /v2/reflector/syslog-servers/:id
 * Deletes a syslog server and all its associations (V2 API).
 * @param syslogServerId - Syslog server ID
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 * @param force - Force delete (default: true)
 */
export function deleteSyslogServerV2(
  syslogServerId: number,
  tenantId: number,
  token: string,
  force = true
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString()
  });
  
  return axios.delete(`${API_V2_URL}/reflector/syslog-servers/${syslogServerId}?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
    data: { force },
  });
}

/**
 * DELETE /v2/reflector/syslog-associations/by-mode
 * Deletes a syslog association by mode (standalone or cluster) (V2 API).
 * @param associationId - Association ID (required for standalone mode)
 * @param associationIds - Array of association IDs (required for cluster mode, sent as associationId1, associationId2, etc.)
 * @param mode - Mode: 'standalone' or 'cluster'
 * @param reflectorInstanceId - Reflector instance ID (required for standalone mode)
 * @param clusterId - Cluster ID (required for cluster mode)
 * @param tenantId - Tenant ID
 * @param token - Authentication token
 */
export function deleteSyslogAssociationV2(
  associationId: number | undefined,
  mode: 'standalone' | 'cluster',
  tenantId: number,
  token: string,
  reflectorInstanceId?: number,
  clusterId?: number,
  associationIds?: number[]
) {
  const params = new URLSearchParams({
    tenantId: tenantId.toString(),
    mode: mode
  });
  
  if (mode === 'standalone' && reflectorInstanceId) {
    params.append('reflectorInstanceId', reflectorInstanceId.toString());
    // For standalone mode, associationId is required
    if (associationId !== undefined) {
      params.append('associationId', associationId.toString());
    }
  } else if (mode === 'cluster' && clusterId) {
    // For cluster mode, send clusterId and all associationIds as associationId1, associationId2, etc.
    // API requires at least associationId1 and associationId2 for cluster mode (2 reflector instances)
    params.append('clusterId', clusterId.toString());
    if (associationIds && associationIds.length > 0) {
      // Ensure we have at least 2 association IDs for cluster mode
      // If only one is provided, duplicate it (both instances should have the same association)
      const idsToSend = associationIds.length >= 2 
        ? associationIds 
        : [associationIds[0], associationIds[0]]; // Duplicate if only one
      
      idsToSend.forEach((id, index) => {
        params.append(`associationId${index + 1}`, id.toString());
      });
    }
  }
  
  return axios.delete(`${API_V2_URL}/reflector/syslog-associations/by-mode?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}
